import { OrderTypeEnum } from '@flipster/apis/types';
import { ConditionalOrderTypeEnum, type Order, type PendingPosition } from '@flipster/r230522/original/order';
import Num from '@flipster/utils/num';

export type TpslSide = 'tp' | 'sl';

/**
 * 주문에서 뽑는 기본 플래그.
 *
 * - `isLimitType`: Limit 탭에 올라가는 행인지
 * - `isTpsl`: 포지션 TP/SL 주문인지
 * - `tpslType`: TP인지 SL인지 (`isTpsl`일 때만 의미 있음)
 */
export function getPendingOrderRoles(order: Pick<Order, 'orderType' | 'conditionalOrderType' | 'waitingTrigger'>) {
  const isTpsl =
    order.conditionalOrderType === ConditionalOrderTypeEnum.PositionTp ||
    order.conditionalOrderType === ConditionalOrderTypeEnum.PositionSl;
  // Limit 탭 = 일반 Limit + 발동된 Limit TP/SL + 발동된 Trigger Limit(`waitingTrigger === false`).
  // 발동 전 Trigger Limit은 Trigger 탭에 남는다 (ERD-216).
  const isLimitType =
    order.orderType === OrderTypeEnum['Limit'] ||
    (order.orderType === OrderTypeEnum['StopLimit'] && (isTpsl || order.waitingTrigger === false));
  const tpslType: TpslSide = order.conditionalOrderType === ConditionalOrderTypeEnum.PositionSl ? 'sl' : 'tp';
  return { isLimitType, isTpsl, tpslType };
}

/**
 * 이 행이 어떤 주문 행인지. 행 라우팅·가격 표시·가격 편집 가능 여부가 모두 여기서 갈린다.
 *
 * ```
 * Limit 탭 — 이미 호가창에 있음. 가격 1개(Limit price)
 *  ├─ limit          일반 Limit, 발동된 Trigger Limit
 *  └─ limitTpsl      Limit (TP) / Limit (SL)
 * Trigger 탭 — 발동 전. 가격 2개(Trigger price + 발동 후 나갈 Order price)
 *  ├─ triggerLimit   발동되면 지정가(또는 BBO)로 나감
 *  └─ triggerMarket  발동되면 시장가로 나감
 * ```
 */
export type PendingOrderKind = 'limit' | 'limitTpsl' | 'triggerLimit' | 'triggerMarket';

/**
 * TP/SL의 Market·Limit 구분은 `orderType`으로 본다 — `orderTp/SlExecutionType`은 bracket 주문에만
 * 값이 있다 (ERD-212 §3.8).
 */
export function getPendingOrderKind(
  order: Pick<Order, 'orderType' | 'conditionalOrderType' | 'waitingTrigger'>,
): PendingOrderKind {
  const { isTpsl, isLimitType } = getPendingOrderRoles(order);
  const isStopLimit = order.orderType === OrderTypeEnum['StopLimit'];
  if (isTpsl && isStopLimit) return 'limitTpsl';
  if (isLimitType) return 'limit';
  return isStopLimit ? 'triggerLimit' : 'triggerMarket';
}

/** Limit 탭 행인지. Trigger 탭 행이면 false. */
export function isPendingLimitTabKind(kind: PendingOrderKind): boolean {
  return kind === 'limit' || kind === 'limitTpsl';
}

/** 가격 칸에 보여줄 것: "BBO" 라벨 / "Market" 라벨 / 숫자 가격. */
export type PendingPriceDisplay = 'bbo' | 'market' | 'price';

/**
 * Limit 계열 가격 칸의 표기.
 *
 * BBO 주문도 호가에 안착하면 RE가 결정한 Limit price가 확정된다(BBO는 반대편 best price로 값이 매겨지므로,
 * 목록에 남는 상태는 사실상 최상단 물량을 넘겨 부분체결된 잔량이다). 그래서 `priceMatchStrategy`만 보고
 * 라벨을 고정하지 않고 확정된 가격을 우선 보여준다 (QA-21295). 앱(iOS/Android)도 같은 기준으로, 가격이
 * 실제로 없는 발동 전 Trigger Limit에만 "BBO"를 노출한다.
 */
function getLimitPriceDisplayType(order: Pick<PendingPosition, 'priceMatchStrategy' | 'price'>): PendingPriceDisplay {
  const hasPrice = !!order.price && !Num(order.price).isZero();
  if (hasPrice) return 'price';
  // 가격이 아직 안 내려온 순간(BBO 체결 직후 WS 랙 등)에만 BBO 주문을 라벨로 대체한다.
  return order.priceMatchStrategy ? 'bbo' : 'price';
}

/** 행의 대표 가격 칸 — Limit 탭은 Limit price, Trigger 탭은 Trigger price. */
export function getPendingPriceDisplayType(
  order: Pick<
    PendingPosition,
    'orderType' | 'conditionalOrderType' | 'waitingTrigger' | 'priceMatchStrategy' | 'triggerTimestamp' | 'price'
  >,
): PendingPriceDisplay {
  switch (getPendingOrderKind(order)) {
    case 'limit':
    case 'limitTpsl':
      return getLimitPriceDisplayType(order);
    case 'triggerLimit':
    case 'triggerMarket':
      // time-trigger 주문은 예약 시점에 시장가로 나가므로 "Market".
      return order.triggerTimestamp ? 'market' : 'price';
  }
}

/** Trigger 행의 Order price 칸 — 발동되면 나갈 가격. 항상 read-only라 편집 함수와 짝이 없다. */
export function getPendingOrderPriceDisplayType(
  order: Pick<
    PendingPosition,
    'orderType' | 'conditionalOrderType' | 'waitingTrigger' | 'priceMatchStrategy' | 'price'
  >,
): PendingPriceDisplay {
  switch (getPendingOrderKind(order)) {
    case 'triggerLimit':
      if (order.priceMatchStrategy) return 'bbo';
      return order.price && !Num(order.price).isZero() ? 'price' : 'market';
    case 'triggerMarket':
      return 'market';
    case 'limit':
    case 'limitTpsl':
      // Limit 탭엔 이 칸이 없어 호출되지 않는다. 잘못 불려도 틀린 "Market"이 나오지 않게 대표 가격과 같게 둔다.
      return getLimitPriceDisplayType(order);
  }
}

/** 대표 가격을 편집할 수 있는지(= `AdjustOrderPopover`로 감쌀지). Limit (TP)/(SL)만 불가. */
export function isPendingPriceEditable(
  order: Pick<Order, 'orderType' | 'conditionalOrderType' | 'waitingTrigger'>,
): boolean {
  switch (getPendingOrderKind(order)) {
    case 'limit':
    case 'triggerLimit':
    case 'triggerMarket':
      return true;
    case 'limitTpsl':
      // TP/SL 가격은 TP/SL 관리 UI에서만 바꾼다(삭제 후 재생성). 주문 amend로 고치면 설정과 어긋난다.
      // QA-21297, ERD-212 §2.3
      return false;
  }
}
